import { ref, watch, onMounted, onBeforeUnmount, nextTick } from '#imports'
import { useRoute, useRouter, useNuxtApp } from '#imports'

/**
 * Composable for handling vehicle quick view functionality
 * Manages the quick view state and URL synchronization
 */
export const useVehicleQuickView = () => {
  const route = useRoute()
  const router = useRouter()
  const quickViewVehicleId = ref<string | null>(null)
  const isQuickViewOpen = ref(false)
  const isInitialized = ref(false)
  const lastScrollPosition = ref(0)

  // Handler function for watcher logic
  const handleQuickViewRouteChange = (newQuickView: string | string[] | undefined | null) => {
    const quickViewId = Array.isArray(newQuickView) ? newQuickView[0] : newQuickView; // Handle potential array
    if (quickViewId) {
      quickViewVehicleId.value = quickViewId as string;
      isQuickViewOpen.value = true;
    } else {
      // Only close if it was previously open
      if (isQuickViewOpen.value) {
        quickViewVehicleId.value = null;
        isQuickViewOpen.value = false;
      }
    }
  };

  // Function to open quick view
  const openQuickView = async (vehicleId: string) => {
    // Store current scroll position
    lastScrollPosition.value = window.scrollY
    
    // Update URL with quickView parameter
    const query = { ...route.query, quickView: vehicleId }
    await router.replace({ query })
    
    // Update state
    quickViewVehicleId.value = vehicleId
    isQuickViewOpen.value = true
  }

  // Function to close quick view
  const closeQuickView = async () => {
    // Remove quickView from URL
    const query = { ...route.query }
    delete query.quickView
    await router.replace({ query })
    
    // Update state
    isQuickViewOpen.value = false
    quickViewVehicleId.value = null
    
    // Restore scroll position after closing
    await nextTick()
    window.scrollTo({
      top: lastScrollPosition.value,
      behavior: 'instant'
    })
  }

  // Watch for route query changes (NO immediate)
  watch(() => route.query.quickView, (newQuickView) => {
    handleQuickViewRouteChange(newQuickView);
  }, { immediate: true })

  // Initialize on mount
  onMounted(() => {
    isInitialized.value = true
    // Manually run the handler logic once after mount, wrapped in nextTick
    nextTick(() => {
      // LAST RESORT: Directly parse window.location.search
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const quickViewParam = searchParams.get('quickView');
        handleQuickViewRouteChange(quickViewParam); // Pass the directly parsed value
      } catch (e) {
        // Fallback attempt using router state if direct parse fails
        const nuxtApp = useNuxtApp();
        const currentQuery = nuxtApp.$router.currentRoute.value.query;
        handleQuickViewRouteChange(currentQuery.quickView as string | string[] | null | undefined);
      }
    });
  })

  // Cleanup on unmount
  onBeforeUnmount(() => {
    isInitialized.value = false
    lastScrollPosition.value = 0
  })

  return {
    quickViewVehicleId,
    isQuickViewOpen,
    openQuickView,
    closeQuickView,
    isInitialized
  }
}
